/**
 * Chart section with multi-asset tab selector.
 * Renders either a per-asset chart, portfolio chart, or single-asset chart.
 */

import { useState, useEffect } from 'react';
import { Chart } from '../Chart';
import { PortfolioChart } from '../Chart/PortfolioChart';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { getFrShortThreshold, getFrLongThreshold, configDateToTimestamp } from '../../utils/frThresholds';
import type { ApiBacktestResult, AggregateBacktestResult, Timeframe } from '../../types';
import type { MultiAssetItem } from '../../hooks/useMultiAsset';

// ── Delete button with inline confirmation ──────────────────────────────────

interface DeleteRunButtonProps {
  onDeleteRun: () => void;
}

function DeleteRunButton({ onDeleteRun }: DeleteRunButtonProps) {
  const [confirming, setConfirming] = useState(false);

  if (confirming) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <button
          aria-label="Confirm delete"
          onClick={() => {
            setConfirming(false);
            onDeleteRun();
          }}
          style={{
            padding: '3px 10px',
            background: '#c0392b',
            border: 'none',
            borderRadius: 5,
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Confirm delete
        </button>
        <button
          aria-label="Cancel"
          onClick={() => setConfirming(false)}
          style={{
            padding: '3px 8px',
            background: 'none',
            border: '1px solid #444',
            borderRadius: 5,
            color: '#aaa',
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </span>
    );
  }

  return (
    <button
      aria-label="Delete run"
      title="Delete this run"
      onClick={() => setConfirming(true)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 8px',
        background: 'none',
        border: '1px solid #3d2222',
        borderRadius: 5,
        color: '#c0392b',
        fontSize: 12,
        cursor: 'pointer',
        transition: 'border-color 0.1s',
      }}
      onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.borderColor = '#c0392b')}
      onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.borderColor = '#3d2222')}
    >
      {/* Trash icon (minimal inline SVG) */}
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      </svg>
      Delete run
    </button>
  );
}

function useChartHeight(): number {
  const [height, setHeight] = useState(() => window.innerWidth < 768 ? 300 : 450);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerWidth < 768 ? 300 : 450);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return height;
}

interface ChartSectionProps {
  currentResult: ApiBacktestResult | null;
  isMultiAsset: boolean;
  multiAssets: MultiAssetItem[];
  selectedAssetIndex: number;
  onSelectAsset: (idx: number) => void;
  selectedAsset: MultiAssetItem | null;
  assetCandles: ApiBacktestResult['candles'] | undefined;
  /**
   * SHA-43: When provided, a "Delete run" button appears in the header and calls
   * this callback after the user confirms. The parent is responsible for calling
   * the delete mutation — ChartSection only handles the UI affordance.
   */
  onDeleteRun?: () => void;
}

export function ChartSection({
  currentResult,
  isMultiAsset,
  multiAssets,
  selectedAssetIndex,
  onSelectAsset,
  selectedAsset,
  assetCandles,
  onDeleteRun,
}: ChartSectionProps) {
  const result = currentResult as ApiBacktestResult;
  const chartHeight = useChartHeight();

  return (
    <section>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-y-1">
        <h2 className="text-lg font-semibold text-white">
          {isMultiAsset ? 'Multi-Asset Portfolio' : 'Chart'}
        </h2>
        {currentResult && (
          <div className="flex items-center flex-wrap gap-2 md:gap-4 text-sm text-gray-400">
            <span>
              {isMultiAsset && selectedAsset
                ? `${selectedAsset.label} / ${selectedAsset.timeframe}`
                : isMultiAsset
                  ? `${multiAssets.length} assets`
                  : `${result.config.symbol} / ${currentResult.config.timeframe}`}
            </span>
            <span>
              {new Date(currentResult.config.startDate).toLocaleDateString()}{' '}
              -{' '}
              {new Date(currentResult.config.endDate).toLocaleDateString()}
            </span>
            <span className="hidden sm:inline">
              {isMultiAsset && selectedAsset
                ? `${assetCandles?.length ?? 0} candles`
                : isMultiAsset
                  ? `${currentResult.trades.length} trades`
                  : `${result.candles?.length ?? 0} candles`}
            </span>
            {/* SHA-43: delete button — only shown when the parent wires the handler */}
            {onDeleteRun && <DeleteRunButton onDeleteRun={onDeleteRun} />}
          </div>
        )}
      </div>

      {/* Multi-asset tab selector */}
      {isMultiAsset && multiAssets.length > 0 && (
        <div className="flex items-center gap-1 mb-3 flex-wrap">
          <button
            onClick={() => onSelectAsset(-1)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              selectedAssetIndex === -1
                ? 'bg-primary-600 text-white'
                : 'bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600'
            }`}
          >
            Portfolio
          </button>
          {multiAssets.map((asset, idx) => (
            <button
              key={asset.symbol}
              onClick={() => onSelectAsset(idx)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                selectedAssetIndex === idx
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600'
              }`}
            >
              {asset.label} ({asset.timeframe})
            </button>
          ))}
        </div>
      )}

      <ErrorBoundary label="Chart">
        {isMultiAsset && selectedAsset ? (
          /* Multi-asset with specific asset selected */
          <>
            {/* Per-asset return metrics are isolated-margin display (floored at 0).
                They cannot be summed to reconstruct portfolio equity — use the
                Portfolio tab for overall performance. */}
            <p className="text-xs text-gray-500 mb-1 italic">
              Per-asset equity: isolated display (floored at -100%) — does not sum to portfolio equity.
            </p>
            {assetCandles && assetCandles.length > 0 ? (
              <Chart
                candles={assetCandles}
                trades={currentResult!.trades.filter((t) => t.symbol === selectedAsset.symbol)}
                height={chartHeight}
                isFutures={true}
                backtestTimeframe={selectedAsset.timeframe as Timeframe}
                exchange={result.config.exchange}
                symbol={selectedAsset.symbol}
                startDate={configDateToTimestamp(result.config.startDate)}
                endDate={configDateToTimestamp(result.config.endDate)}
                rollingMetrics={(currentResult as AggregateBacktestResult).perAssetResults?.[selectedAsset.symbol]?.rollingMetrics}
                frShortThreshold={getFrShortThreshold(result, selectedAsset.symbol)}
                frLongThreshold={getFrLongThreshold(result, selectedAsset.symbol)}
                indicators={(currentResult as AggregateBacktestResult).perAssetResults?.[selectedAsset.symbol]?.indicators}
              />
            ) : (
              <div
                className="bg-gray-800 rounded-lg flex items-center justify-center text-gray-500"
                style={{ height: chartHeight }}
              >
                Loading candles for {selectedAsset.label}...
              </div>
            )}
          </>
        ) : isMultiAsset ? (
          /* Portfolio view - equity curve for the whole portfolio */
          <PortfolioChart
            equity={currentResult!.equity ?? []}
            rollingMetrics={currentResult!.rollingMetrics}
            trades={currentResult!.trades}
            height={chartHeight}
          />
        ) : (
          /* Single-asset chart */
          <Chart
            candles={result?.candles ?? []}
            trades={currentResult?.trades ?? []}
            height={chartHeight}
            isFutures={
              currentResult?.config?.mode === 'futures' ||
              result?.metrics?.totalFundingIncome !== undefined
            }
            backtestTimeframe={result?.config.timeframe}
            exchange={result?.config.exchange}
            symbol={result?.config.symbol}
            startDate={configDateToTimestamp(result?.config.startDate)}
            endDate={configDateToTimestamp(result?.config.endDate)}
            rollingMetrics={result?.rollingMetrics}
            frShortThreshold={getFrShortThreshold(result)}
            frLongThreshold={getFrLongThreshold(result)}
            indicators={result?.indicators}
          />
        )}
      </ErrorBoundary>
    </section>
  );
}
